import React from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";

/**
 * TikTok OAuth2 callback page.
 * TikTok redirects here after the user approves the authorization request.
 * URL format: /tiktok/callback?code=...&state=...
 *
 * This page extracts the code + state, calls POST /TikTok/OAuth/Callback,
 * then redirects to /tiktok (or the returnUrl from the server).
 */
export default function TikTokCallback() {
  const api = useApi();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<"processing" | "success" | "error">("processing");
  const [errorMsg, setErrorMsg] = React.useState("");

  React.useEffect(() => {
    async function handleCallback() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const error = params.get("error");
      const errorDescription = params.get("error_description");

      if (error) {
        setErrorMsg(errorDescription ?? error ?? "Authorization denied.");
        setStatus("error");
        return;
      }

      if (!code || !state) {
        setErrorMsg("Missing authorization parameters. Please try connecting again.");
        setStatus("error");
        return;
      }

      try {
        const resp = await api.messaging.post("TikTok/OAuth/Callback", { code, state });
        const data = await resp.json();

        if (!resp.ok) {
          setErrorMsg(data?.error ?? "Authorization failed. Please try again.");
          setStatus("error");
          return;
        }

        setStatus("success");

        // Navigate to return URL or default TikTok page
        const returnUrl = data?.returnUrl ?? "/tiktok";
        setTimeout(() => navigate(returnUrl), 1500);
      } catch {
        setErrorMsg("Failed to complete TikTok authorization. Please try again.");
        setStatus("error");
      }
    }

    handleCallback();
  }, []); // run once on mount

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-md w-full text-center">
        {/* TikTok logo */}
        <img src="/img/tiktok.svg" alt="TikTok" className="w-14 h-14 rounded-xl mx-auto mb-4" />

        {status === "processing" && (
          <>
            <h1 className="text-xl font-semibold text-slate-800 mb-2">Connecting TikTok...</h1>
            <p className="text-slate-500 text-sm">
              Completing authorization. This will only take a moment.
            </p>
            <div className="mt-6 flex justify-center">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-check text-green-600 text-xl" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-semibold text-slate-800 mb-2">TikTok Connected!</h1>
            <p className="text-slate-500 text-sm">
              Your TikTok Business account has been connected successfully. Redirecting...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-times text-red-600 text-xl" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-semibold text-slate-800 mb-2">Connection Failed</h1>
            <p className="text-red-600 text-sm mb-4">{errorMsg}</p>
            <button
              className="text-blue-600 underline text-sm"
              onClick={() => navigate("/tiktok")}
            >
              Return to TikTok settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
